// src/pages/Dashboard.tsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line,
} from "recharts";
import { useCampaignId } from "../hooks/useCampaignId";

type Contact = { id: string; contacted_at: string };

// We only load a small recent slice of voters for charts that need timestamps.
// Totals & breakdown are fetched via RPCs (no 1,000 cap).
type RecentVoter = {
  id: string;
  support_level: string | null;
  updated_at?: string | null;
  created_at?: string | null;
  sentiment_changed_at?: string | null;
};

const CARDS_BG = "#ffffff";
const PANEL_BG = "#ffffff";
const RADIUS = 14;

const SENTIMENT_COLORS: Record<string, string> = {
  Strong: "#22c55e",
  Lean: "#86efac",
  Undecided: "#facc15",
  "Lean Against": "#f97316",
  "Strong Against": "#ef4444",
};

// ---- Date helpers (TZ-safe for grouping by **local** calendar day) ----
function startOfDay(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d = new Date()) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function daysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
}
function weekLabel(d: Date) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function ymdLocal(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function toLocalYMD(isoLike: string | null | undefined) {
  if (!isoLike) return "";
  return ymdLocal(new Date(isoLike));
}

export default function Dashboard() {
  const campaignId = useCampaignId(); // single source of truth

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [recentVoters, setRecentVoters] = useState<RecentVoter[]>([]);
  const [totalVoters, setTotalVoters] = useState<number>(0);
  const [bySupport, setBySupport] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!campaignId) return;
    setLoading(true);
    setErr(null);

    (async () => {
      try {
        // Keep the “recent slice” tight to avoid timeouts (28d is plenty for charts)
        const sinceISO = daysAgo(28).toISOString();

        // 1) Contacts (last 28–35 days) — directly from table, but it’s indexed
        const contactsRes = await supabase
          .from("voter_contacts")
          .select("id, contacted_at")
          .eq("campaign_id", campaignId)
          .gte("contacted_at", sinceISO)
          .order("contacted_at", { ascending: false });

        if (contactsRes.error) throw contactsRes.error;
        setContacts((contactsRes.data ?? []) as Contact[]);

        // 2) Totals via RPCs (no 1,000 limit; both are indexed server-side)
        const [totalRes, countsRes] = await Promise.all([
          supabase.rpc("get_campaign_voter_total", { p_campaign: campaignId }),
          supabase.rpc("get_campaign_voter_counts", { p_campaign: campaignId }),
        ]);
        if (totalRes.error) throw totalRes.error;
        if (countsRes.error) throw countsRes.error;

        setTotalVoters(Number(totalRes.data ?? 0));

        // Map counts into a quick lookup (treat null as 'Undecided')
        const countsArr: Array<{ support_level: string | null; n: number }> =
          (countsRes.data as any[]) ?? [];
        const map: Record<string, number> = {};
        for (const row of countsArr) {
          const key = (row.support_level ?? "Undecided").trim();
          map[key] = (map[key] ?? 0) + Number(row.n ?? 0);
        }
        setBySupport(map);

        // 3) Small recent-slice of voters (time-based charts only)
        //    Filters exclusively on sentiment_changed_at to match the index.
        const recentRes = await supabase
          .from("campaign_voters")
          .select("id, support_level, updated_at, created_at, sentiment_changed_at")
          .eq("campaign_id", campaignId)
          .gte("sentiment_changed_at", sinceISO)
          .limit(5000);

        if (recentRes.error) throw recentRes.error;
        setRecentVoters((recentRes.data ?? []) as RecentVoter[]);

        setLoading(false);
      } catch (e: any) {
        setErr(e?.message ?? "Failed to load dashboard");
        setLoading(false);
      }
    })();
  }, [campaignId]);

  // --- Derived metrics ---
  const SUPPORT = useMemo(() => new Set(["Strong", "Lean"]), []);

  const contactsToday = useMemo(() => {
    const start = startOfDay().toISOString();
    return contacts.filter((c) => c.contacted_at >= start).length;
  }, [contacts]);

  // Use RPC counts (Strong + Lean)
  const supportersNow = useMemo(() => {
    return (bySupport["Strong"] ?? 0) + (bySupport["Lean"] ?? 0);
  }, [bySupport]);

  const supportRate = useMemo(() => {
    const total = totalVoters || 1;
    return Math.round((supportersNow / total) * 1000) / 10;
  }, [supportersNow, totalVoters]);

  // Switched to Supporting (7d) — computed from recent slice only
  const switchedThisWeek = useMemo(() => {
    const sinceLocalYMD = ymdLocal(daysAgo(7));
    return recentVoters.filter((v) => {
      const isSupport = SUPPORT.has((v.support_level ?? "").trim());
      if (!isSupport) return false;
      const stampYMD = toLocalYMD(v.sentiment_changed_at ?? v.updated_at ?? v.created_at);
      return stampYMD >= sinceLocalYMD;
    }).length;
  }, [recentVoters, SUPPORT]);

  // Sentiment breakdown from RPC counts (no cap)
  const sentimentBreakdown = useMemo(() => {
    const keys = ["Strong", "Lean", "Undecided", "Lean Against", "Strong Against"];
    return keys.map((k) => ({ name: k, value: bySupport[k] ?? 0 }));
  }, [bySupport]);

  // Daily Contact Activity + New Supporters (last 7 local days)
  const dailyContacts = useMemo(() => {
    const map: Record<string, { day: string; contacts: number; conversions: number }> = {};
    for (let i = 6; i >= 0; i--) {
      const d = daysAgo(i);
      const key = ymdLocal(d);
      map[key] = {
        day: d.toLocaleDateString(undefined, { weekday: "short" }),
        contacts: 0,
        conversions: 0,
      };
    }

    // Contacts: local-day bucket
    contacts.forEach((c) => {
      const key = toLocalYMD(c.contacted_at);
      if (map[key]) map[key].contacts += 1;
    });

    // Conversions: local-day using the recent slice only
    recentVoters.forEach((v) => {
      if (!SUPPORT.has((v.support_level ?? "").trim())) return;
      const stampYMD = toLocalYMD(v.sentiment_changed_at ?? v.updated_at ?? v.created_at);
      if (map[stampYMD]) map[stampYMD].conversions += 1;
    });

    return Object.values(map);
  }, [contacts, recentVoters, SUPPORT]);

  // Supporter Growth Trend (weekly checkpoints)
  // Uses recent conversions + final point forced to supportersNow (from RPC counts).
  const growthTrend = useMemo(() => {
    const checkpoints: Date[] = [];
    for (let w = 4; w >= 0; w--) checkpoints.push(endOfDay(daysAgo(w * 7)));

    // Build incremental conversions-by-day from recent slice
    const convByDay: Record<string, number> = {};
    recentVoters.forEach((v) => {
      if (!SUPPORT.has((v.support_level ?? "").trim())) return;
      const ymd = toLocalYMD(v.sentiment_changed_at ?? v.updated_at ?? v.created_at);
      if (!ymd) return;
      convByDay[ymd] = (convByDay[ymd] ?? 0) + 1;
    });

    // Accumulate conversions up to each checkpoint; anchor last point to supportersNow
    const points = checkpoints.map((d) => {
      const cutoffYMD = ymdLocal(d);
      let sum = 0;
      for (const [day, n] of Object.entries(convByDay)) {
        if (day <= cutoffYMD) sum += n;
      }
      return { label: weekLabel(d), value: sum };
    });

    if (points.length > 0) {
      // Force last point to true current supporters (RPC), so the chart ends correct
      points[points.length - 1].value = supportersNow;
      // Ensure non-decreasing
      for (let i = 1; i < points.length; i++) {
        if (points[i].value < points[i - 1].value) points[i].value = points[i - 1].value;
      }
    }

    return points;
  }, [recentVoters, SUPPORT, supportersNow]);

  if (!campaignId) return <div style={{ padding: 24 }}>Select a campaign from the top-right to view the dashboard.</div>;
  if (loading) return <div style={{ padding: 24 }}>Loading dashboard…</div>;
  if (err) return <div style={{ padding: 24, color: "crimson" }}>Error: {err}</div>;

  return (
    <div style={{ padding: "24px 28px", background: "#f3f7fb", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, lineHeight: 1.05, fontSize: "clamp(22px, 6vw, 32px)", letterSpacing: -0.2 }}>
          Voter Sentiment Dashboard
        </h1>
        <div style={{ color: "#6b7280", marginTop: 6, fontSize: 14 }}>
          Real-time insights for your campaign
        </div>
      </div>

      {/* Top cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 }}>
        <Card title="Contacted Today" value={contactsToday} sub="+ vs. yesterday" />
        <Card title="Switched to Supporting (7d)" value={switchedThisWeek} sub="recent conversions" />
        <Card title="Running Total Supporters" value={supportersNow} sub="Strong + Lean" />
        <Card title="Support Rate" value={`${supportRate}%`} sub="supporters / voters" />
      </div>

      {/* Middle row */}
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.8fr", gap: 12, marginBottom: 16 }}>
        <div style={{ background: PANEL_BG, borderRadius: RADIUS, padding: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Voter Sentiment by Status</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", alignItems: "center", gap: 8 }}>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 10 }}>
                  <Pie data={sentimentBreakdown} dataKey="value" nameKey="name" innerRadius={60} outerRadius={92} paddingAngle={2} cx={"50%"}>
                    {sentimentBreakdown.map((item, index) => (
                      <Cell key={item.name ?? index} fill={SENTIMENT_COLORS[item.name] ?? "#8884d8"} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {sentimentBreakdown.map((s) => (
                <li key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, margin: "6px 0", fontSize: 14 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: SENTIMENT_COLORS[s.name] ?? "#8884d8" }} />
                  <span style={{ flex: 1, minWidth: 90 }}>{s.name}</span>
                  <strong>{s.value}</strong>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div style={{ background: PANEL_BG, borderRadius: RADIUS, padding: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Daily Contact Activity</div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyContacts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="contacts" name="Contacts" />
                <Bar dataKey="conversions" name="New Supporters" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div style={{ background: PANEL_BG, borderRadius: RADIUS, padding: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Supporter Growth Trend</div>
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={growthTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="value" name="Supporters" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Card({ title, value, sub }: { title: string; value: number | string; sub?: string }) {
  return (
    <div style={{ background: CARDS_BG, borderRadius: RADIUS, padding: 14, boxShadow: "0 1px 2px rgba(0,0,0,0.03)" }}>
      <div style={{ color: "#6b7280", fontSize: 14 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>{value}</div>
      {sub ? <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 4 }}>{sub}</div> : null}
    </div>
  );
}
